import { login as apiLogin, getCurrentUser as apiGetUser } from '@/lib/api/auth';
import { getCustomers as apiGetCustomers } from '@/lib/api/customers';
import { tokenManager } from './tokenManager';
import { setUserContext, setCustomerContext } from '@/lib/sentry';
import { logAuth, logCustomer } from '@/lib/breadcrumbs';
import type { User, Customer, ClientUser } from '@/contexts/auth/types';
import api from '@/lib/api/core';
import { toClientUser } from '@/contexts/auth/utils/userUtils';
import axios from 'axios';
import { logDebug } from '@/utils/debug';

/**
 * Authentication Service
 * 
 * Centralizes authentication logic while using the existing API endpoints.
 * This service integrates with the existing API utilities from lib/api/
 */

/**
 * Format error messages to be user-friendly
 */
const formatAuthError = (error: any): string => {
  // Handle Axios errors
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    
    // Common authentication errors
    if (status === 401) {
      return 'Invalid email or password. Please check your credentials and try again.';
    }
    
    if (status === 403) {
      return 'You do not have permission to access this resource.';
    }
    
    if (status === 404) {
      return 'Authentication service not found. Please try again later.';
    }
    
    if (status === 500) {
      return 'Server error occurred. Please try again later or contact support.';
    }
    
    if (error.message === 'Network Error') {
      return 'Unable to connect to the authentication server. Please check your internet connection.';
    }
    
    // Use server message if available
    const serverMessage = error.response?.data?.detail || error.response?.data?.message;
    if (serverMessage) {
      return serverMessage;
    }
  }
  
  // For non-axios errors or if we can't extract a specific message
  return 'Authentication failed. Please check your credentials and try again.';
};

/**
 * Login a user with email and password
 */
export const login = async (email: string, password: string): Promise<{
  success: boolean;
  user?: User;
  error?: string;
}> => {
  try {
    // Clear any existing session data first to ensure a fresh start
    clearSession();
    
    // Use the existing login function from lib/api/auth.ts
    const tokenResponse = await apiLogin(email, password);
    // Note: The token is already saved to localStorage in the apiLogin function
    
    // With the token set, get the user data using the existing API function
    const userResponse = await apiGetUser();
    
    // Convert API response to our User type, matching the existing type structure
    const user: User = {
      id: userResponse.id,
      email: userResponse.email,
      first_name: userResponse.first_name || '',
      last_name: userResponse.last_name || '',
      is_superuser: userResponse.is_superuser,
      last_active_customer: userResponse.last_active_customer,
      profile: userResponse.profile || { theme: 'light' }
    };
    
    // Store user in localStorage for session persistence - using ClientUser
    localStorage.setItem('user', JSON.stringify(toClientUser(user)));
    
    // Log successful login
    logAuth('Login successful', { email });
    
    // Set user context for analytics
    setUserContext(user);
    
    return {
      success: true,
      user
    };
  } catch (error) {
    // Log failed login attempt
    logAuth('Login failed', { email, error: String(error) });
    
    // Convert technical error to user-friendly message
    const formattedError = formatAuthError(error);
    
    return {
      success: false,
      error: formattedError
    };
  }
};

/**
 * Check if the user has a valid session
 */
export const checkSession = async (): Promise<{
  isValid: boolean;
  user: User | null;
  activeCustomer: Customer | null;
}> => {
  try {
    // Check if tokens exist
    if (!tokenManager.hasTokens()) {
      return { isValid: false, user: null, activeCustomer: null };
    }
    
    // Verify token is valid by making an API call
    const userResponse = await apiGetUser();
    
    // Convert API response to our User type
    const user: User = {
      id: userResponse.id,
      email: userResponse.email,
      first_name: userResponse.first_name || '',
      last_name: userResponse.last_name || '',
      is_superuser: userResponse.is_superuser,
      last_active_customer: userResponse.last_active_customer,
      profile: userResponse.profile || { theme: 'light' }
    };
    
    // Store user in localStorage - using ClientUser to strip sensitive information
    localStorage.setItem('user', JSON.stringify(toClientUser(user)));
    
    // Get active customer from localStorage
    let activeCustomer: Customer | null = null;
    const storedCustomer = localStorage.getItem('active_customer');
    
    if (storedCustomer) {
      try {
        activeCustomer = JSON.parse(storedCustomer);
      } catch (e) {
        // Use the direct logDebug since we don't have the user object yet
        logDebug(null, 'AuthService', 'Error parsing active customer from localStorage:', e);
      }
    }
    
    // Set user context for analytics
    setUserContext(user);
    if (activeCustomer) {
      setCustomerContext(activeCustomer);
    }
    
    return {
      isValid: true,
      user,
      activeCustomer
    };
  } catch (error) {
    logDebug(null, 'AuthService', 'Session validation failed:', error);
    
    // Clear any invalid session data
    clearSession();
    
    return {
      isValid: false,
      user: null,
      activeCustomer: null
    };
  }
};

/**
 * Clear all session data
 */
export const clearSession = (): void => {
  // Clear user context in analytics
  setUserContext(null);
  setCustomerContext(null);
  
  // Remove tokens and user data
  tokenManager.removeTokens();
  localStorage.removeItem('user');
  localStorage.removeItem('customers');
  localStorage.removeItem('active_customer');
  localStorage.removeItem('active_site');
  localStorage.removeItem('recording_status');
  
  // Clear the Authorization header
  delete api.defaults.headers.common['Authorization'];
};

/**
 * Log out the current user
 */
export const logout = async (): Promise<void> => {
  try {
    // Call logout endpoint if needed
    // await api.post('/auth/logout/');
    
    // Clear all session data
    clearSession();
    
    // Log logout event
    logAuth('User logged out');
  } catch (error) {
    logDebug(null, 'AuthService', 'Error during logout:', error);
    // Ensure session is cleared even if API call fails
    clearSession();
  }
};

/**
 * Get the current logged-in user data
 */
export const getCurrentUser = async (): Promise<User> => {
  try {
    // Use the existing API function
    const userResponse = await apiGetUser();
    
    // Convert API response to our User type, matching the existing type structure
    const user: User = {
      id: userResponse.id,
      email: userResponse.email,
      first_name: userResponse.first_name || '',
      last_name: userResponse.last_name || '',
      is_superuser: userResponse.is_superuser,
      last_active_customer: userResponse.last_active_customer,
      profile: userResponse.profile || { theme: 'light' }
    };
    
    return user;
  } catch (error) {
    logDebug(null, 'AuthService', 'Error getting current user:', error);
    throw error;
  }
};

/**
 * Get all available customers for the current user
 */
export const getCustomers = async (): Promise<Customer[]> => {
  try {
    // Use the existing API function
    return await apiGetCustomers();
  } catch (error) {
    logDebug(null, 'AuthService', 'Error getting customers:', error);
    throw error;
  }
};

/**
 * Set the active customer for the current user session
 */
export const setActiveCustomer = async (customerId: string): Promise<void> => {
  try {
    await api.put(`/crm/customers/active/${customerId}/`);
    
    // Log customer change
    logCustomer('Active customer changed', { customerId });
  } catch (error) {
    logDebug(null, 'AuthService', 'Error setting active customer:', error);
    throw error;
  }
};

/**
 * Select a customer and prepare the application state
 */
export const selectCustomer = async (
  customer: Customer,
  customers?: Customer[]
): Promise<{
  success: boolean;
  error?: string;
}> => {
  try {
    // If customers array is provided, verify this customer exists in it
    if (customers?.length) {
      const validCustomer = customers.find(c => c.id === customer.id);
      
      if (!validCustomer) {
        logDebug(null, 'AuthService', `Customer ${customer.id} not found in available customers`);
        return { 
          success: false, 
          error: "Selected customer is not available for this user" 
        };
      }
    }
    
    // Set as active in API
    await setActiveCustomer(customer.id);
    
    // Store in local storage
    localStorage.setItem('active_customer', JSON.stringify(customer));
    
    // Set customer context for analytics
    setCustomerContext(customer);
    
    // Log customer selection
    logCustomer('Customer selected', { 
      customerId: customer.id, 
      customerName: customer.name 
    });
    
    return { success: true };
  } catch (error) {
    logDebug(null, 'AuthService', 'Error selecting customer:', error);
    return { 
      success: false,
      error: `Failed to select customer: ${error}`
    };
  }
};

/**
 * Auto-select a customer based on last active or first available
 */
export const autoSelectCustomer = async (
  user: User | null, 
  customers: Customer[]
): Promise<{
  success: boolean;
  customer?: Customer;
  error?: string;
}> => {
  if (!user || !customers.length) {
    logDebug(null, 'AuthService', 'Cannot auto-select customer: missing user or customer data');
    return {
      success: false,
      error: 'Missing user or customer data'
    };
  }

  try {
    // Try to get the last active customer
    let selectedCustomer = null;
    
    if (user.last_active_customer) {
      selectedCustomer = customers.find(c => c.id === user.last_active_customer);
    }
    
    // If no last active customer or it wasn't found, select the first customer
    if (!selectedCustomer && customers.length > 0) {
      selectedCustomer = customers[0];
    }
    
    if (selectedCustomer) {
      // Select the customer
      const result = await selectCustomer(selectedCustomer, customers);
      
      if (result.success) {
        return {
          success: true,
          customer: selectedCustomer
        };
      }
      
      return {
        success: false,
        error: result.error
      };
    }
    
    return {
      success: false,
      error: 'No customer available to select'
    };
  } catch (error) {
    logDebug(user, 'AuthService', 'Error auto-selecting customer:', error);
    return {
      success: false,
      error: `Error auto-selecting customer: ${error}`
    };
  }
};

/**
 * Get the current session's recording status
 */
export const getRecordingStatus = async (): Promise<{ isRecording: boolean; isPaused: boolean }> => {
  try {
    const storedStatus = localStorage.getItem('recording_status');
    
    if (storedStatus) {
      return JSON.parse(storedStatus);
    }
    
    return { isRecording: false, isPaused: false };
  } catch (error) {
    logDebug(null, 'AuthService', 'Error getting recording status:', error);
    return { isRecording: false, isPaused: false };
  }
};

/**
 * Start a new session recording
 */
export const startRecording = async (): Promise<void> => {
  try {
    // Call recording API or initialize recording service
    // await api.post('/sessions/start-recording');
    
    // Store recording status
    localStorage.setItem('recording_status', JSON.stringify({
      isRecording: true,
      isPaused: false
    }));
  } catch (error) {
    logDebug(null, 'AuthService', 'Error starting recording:', error);
    throw error;
  }
};

/**
 * Pause the current session recording
 */
export const pauseRecording = async (): Promise<void> => {
  try {
    // Call recording API
    // await api.post('/sessions/pause-recording');
    
    // Update recording status
    localStorage.setItem('recording_status', JSON.stringify({
      isRecording: true,
      isPaused: true
    }));
  } catch (error) {
    logDebug(null, 'AuthService', 'Error pausing recording:', error);
    throw error;
  }
};

/**
 * Resume the current session recording
 */
export const resumeRecording = async (): Promise<void> => {
  try {
    // Call recording API
    // await api.post('/sessions/resume-recording');
    
    // Update recording status
    localStorage.setItem('recording_status', JSON.stringify({
      isRecording: true,
      isPaused: false
    }));
  } catch (error) {
    logDebug(null, 'AuthService', 'Error resuming recording:', error);
    throw error;
  }
};

/**
 * Stop the current session recording
 */
export const stopRecording = async (): Promise<void> => {
  try {
    // Call recording API
    // await api.post('/sessions/stop-recording');
    
    // Clear recording status
    localStorage.setItem('recording_status', JSON.stringify({
      isRecording: false,
      isPaused: false
    }));
  } catch (error) {
    logDebug(null, 'AuthService', 'Error stopping recording:', error);
    throw error;
  }
};

// Export auth service as a single object
export const authService = {
  login,
  logout,
  checkSession,
  clearSession,
  getCurrentUser,
  getCustomers,
  setActiveCustomer,
  selectCustomer,
  autoSelectCustomer,
  getRecordingStatus,
  startRecording,
  pauseRecording,
  resumeRecording,
  stopRecording
};